package no.vareflow.web

import no.vareflow.app.domain.*

private val adminState = object {
    var duration = 30
    var custom = ""
    val expanded = mutableSetOf<String>()
}

fun WebApp.renderMore(root: El, user: UserAccount) {
    when (moreView) {
        "users" -> if (user.role == Role.ADMIN) return renderUsers(root, user)
        "audit" -> if (user.role == Role.ADMIN) return renderAudit(root)
        "quality" -> if (canReviewQuality(user)) return renderQuality(root, user)
        "support" -> if (cloudMode) return renderSupport(root)
        "svinn" -> return renderSvinn(root, user)
    }
    moreView = "main"
    root.pageHeader(t("Mer", "More"), t("Profil, administrasjon og innstillinger", "Profile, administration and settings"))
    root.div("card") {
        div("row") {
            div("avatar", user.name.split(' ').mapNotNull { it.firstOrNull()?.uppercase() }.take(2).joinToString(""))
            div("grow") {
                div("card-title", user.name)
                div("muted small", "@${CompanyLogin.displayLogin(user.username)} · ${roleLabel(user.role)}")
            }
        }
        div("muted small", t("Kategorier", "Categories") + ": " + if (user.role == Role.ADMIN) t("alle", "all") else user.allowedCategories.joinToString().ifBlank { t("ingen", "none") })
    }
    if (user.role == Role.ADMIN) {
        root.h2(t("Administrasjon", "Administration"), "section-title")
        root.div("card-grid") {
            navCard(t("Brukere og tilgang", "Users and access"), t("${core.users.count { it.active }} aktive brukere", "${core.users.count { it.active }} active users")) { moreView = "users"; requestRender() }
            navCard(t("Auditlogg", "Audit log"), t("${core.auditEvents.size} hendelser", "${core.auditEvents.size} events")) { moreView = "audit"; requestRender() }
        }
    }
    root.h2(t("Verktøy", "Tools"), "section-title")
    root.div("card-grid") {
        navCard(t("Språk", "Language"), I18n.language.nativeName) { showLanguagePicker() }
        navCard(t("Utseende", "Appearance"), t("Fargetema: ${WEB_THEMES.first { it.key == loadTheme(user.id) }.label}", "Colour theme: ${WEB_THEMES.first { it.key == loadTheme(user.id) }.label}")) { showThemePicker(user) }
        navCard(t("Utskriftsliste / tellingsark", "Print list / count sheet"), t("Velg hovedvare, papirstørrelse og skala, og skriv ut tomme antallsfelt", "Choose main category, paper size and scale, and print empty quantity fields")) { showPrintDialog() }
        run {
            val month = shownOpenSession()?.yearMonth ?: currentYearMonth()
            val records = core.svinnFor(month)
            val value = if (AccessRules.hasPermission(user, Permission.PRODUCT_VIEW_PRICE)) " · ${formatPrice(records.sumOf { it.value })} kr" else ""
            navCard(t("Svinn", "Svinn"), t("${records.size} registrert i ${formatYearMonth(month)}$value", "${records.size} registered in ${formatYearMonth(month)}$value")) { moreView = "svinn"; requestRender() }
        }
        if (AccessRules.hasPermission(user, Permission.SPOTCHECK_PERFORM)) {
            navCard(t("Stikkprøvekontroll", "Spot check"), t("Valgfri kontroll av et telt produkt; blokkerer ikke avslutning", "Optional check of a counted product; does not block closing")) { showSpotCheck(user) }
        }
        if (canReviewQuality(user)) {
            navCard(t("Datakvalitet", "Data quality"), t("${qualityProducts().size} produkter må kontrolleres", "${qualityProducts().size} products need checking")) { moreView = "quality"; requestRender() }
        }
    }
    root.h2(t("Visning", "Display"), "section-title")
    root.div("card") {
        el("label", "switch-row") {
            div("grow") {
                div("strong", t("Moderne visning (test)", "Modern view (test)"))
                div("muted small", t("Fremdriftsringer, minigrafer på kategorikort, ikon-merker og gradientknapp.", "Progress rings, mini charts on category cards, icon badges and gradient button."))
            }
            el("input", "switch") {
                attr("type", "checkbox")
                attr("role", "switch")
                attr("data-key", "modern-switch")
                node.checked = modernUi
                on("change") {
                    modernUi = node.checked as Boolean
                    saveModernUi(modernUi)
                    lastRings.clear()
                    requestRender()
                }
            }
        }
    }
    if (cloudMode) {
        root.h2(t("Bedrift", "Company"), "section-title")
        root.div("card-grid") {
            val m = cloud.membership
            navCard(t("Bytt bedrift", "Switch company"), t("Nå", "Now") + ": ${m?.companyName.orEmpty()}${if (!m?.companyCode.isNullOrBlank()) " · " + t("Firmakode", "Company code") + " ${m?.companyCode}" else ""}") { showCompanySwitcher() }
            if (user.role == Role.ADMIN) navCard(t("Bedriftsprofil", "Company profile"),
                if (cloud.logo != null) t("Navn og logo (logo lagt inn)", "Name and logo (logo added)") else t("Navn og logo", "Name and logo")) { showCompanyProfileDialog() }
            if (cloud.isOwner) navCard(t("Eier (Garasjestudio)", "Owner (Garasjestudio)"), t("Alle bedrifter, ny bedrift, stenging og support", "All companies, new company, suspension and support")) { openOwnerPage() }
        }
        root.h2(t("Hjelp", "Help"), "section-title")
        root.div("card-grid") {
            val unread = cloud.unreadSupportForMe
            navCard(t("Support", "Support") + if (unread > 0) " · " + t("$unread nytt svar", "$unread new reply") else "",
                t("Skriv til Garasjestudio om feil, spørsmål eller ønsker", "Write to Garasjestudio about bugs, questions or requests")) { moreView = "support"; requestRender() }
        }
        root.h2(t("Synkronisering", "Sync"), "section-title")
        root.div("card-grid") {
            navCard(t("Synkronisering", "Sync"), t("${cloud.state.label} · ${cloud.pendingWrites} i kø · ${cloud.conflicts.size} konflikter", "${cloud.state.label} · ${cloud.pendingWrites} queued · ${cloud.conflicts.size} conflicts")) { showSyncDialog() }
        }
    }
    root.h2(t("Om nettversjonen", "About the web version"), "section-title")
    root.div("card") {
        val company = cloud.membership
        if (company != null) {
            val codeText = if (company.companyCode.isNotBlank()) " (" + t("Firmakode", "company code") + " ${company.companyCode})" else ""
            p(t("Innlogget i ${company.companyName}$codeText. Data deles med telefonene via skyen og lagres også i denne nettleseren, så du kan telle videre uten nett.",
                "Signed in to ${company.companyName}$codeText. Data is shared with the phones via the cloud and also kept in this browser, so you can keep counting offline."), "muted small")
        } else {
            p(t("Dette er testmodus: data lagres bare i denne nettleseren.", "This is test mode: data is stored only in this browser."), "muted small")
        }
        p(t("Innskanning av tellingsark og fakturaimport (OCR) og tale er bare i Android-appen.", "Scanning count sheets, invoice import (OCR) and voice are only in the Android app."), "muted small")
        p(t("Versjon $WEB_VERSION", "Version $WEB_VERSION"), "muted tiny")
    }
    root.button(t("Logg ut", "Log out"), "btn outline block danger-text") { logout() }
}

private fun El.navCard(title: String, subtitle: String, onClick: () -> Unit) {
    el("button", "card nav-card") {
        attr("type", "button")
        div("grow") {
            div("card-title", title)
            div("muted small", subtitle)
        }
        span("chevron", "›").attr("aria-hidden", "true")
        onClick { onClick() }
    }
}

private fun roleLabel(role: Role) = when (role) {
    Role.ADMIN -> "Admin"
    Role.LEADER -> t("Leder", "Leader")
    Role.EMPLOYEE -> t("Ansatt", "Employee")
}

private fun WebApp.renderUsers(root: El, admin: UserAccount) {
    root.button(t("‹ Mer", "‹ More"), "btn text back") { moreView = "main"; requestRender() }
    root.pageHeader(t("Brukere og tilgang", "Users and access"), if (cloudMode) t("Personer i ${cloud.membership?.companyName.orEmpty()}", "People in ${cloud.membership?.companyName.orEmpty()}") else t("Ansatte logger inn i et tidsvindu som admin åpner", "Employees log in during a time window that an admin opens"))
    if (cloudMode) {
        root.div("card") {
            p(if (admin.role == Role.ADMIN) t("Legg til en ny person (også admin), eller trykk Admin på en person under for å gi full tilgang.", "Add a new person (also admin), or tap Admin on a person below to give full access.")
              else t("Legg til en ny ansatt eller leder.", "Add a new employee or leader."), "muted small")
            button(t("+ Ny person", "+ New person"), "btn primary") { showCloudPersonDialog(admin) }
        }
        root.div("card user-card") {
            div("card-title", t("${admin.name} (deg)", "${admin.name} (you)"))
            div("muted small", "@${CompanyLogin.displayLogin(admin.username)} · ${roleLabel(admin.role)}")
        }
        if (core.users.none { it.id != admin.id }) root.p(t("Ingen andre personer ennå. Trykk + Ny person.", "No other people yet. Tap + New person."), "muted small")
    } else root.div("card") {
        div("row") {
            div("grow strong", t("Standard tilgangstid", "Default access time"))
            span("accent strong", "${adminState.duration} min")
        }
        div("chip-row") {
            listOf(15, 30, 60, 120).forEach { min ->
                chip("$min", adminState.duration == min && adminState.custom.isBlank()) {
                    adminState.duration = min; adminState.custom = ""; requestRender()
                }
            }
        }
        field(t("Egendefinert tid (minutter)", "Custom time (minutes)"), adminState.custom, "adm-custom", hint = t("1–10080 min (opptil 7 dager)", "1–10080 min (up to 7 days)")) { raw ->
            val digits = raw.filter { it.isDigit() }.take(5)
            adminState.custom = if ((digits.toIntOrNull() ?: 0) > 10080) "10080" else digits
            adminState.custom.toIntOrNull()?.takeIf { it >= 1 }?.let { adminState.duration = it }
        }.also { it.on("change") { requestRender() } }
        button(t("+ Ny bruker", "+ New user"), "btn primary") { createUserDialog(admin) }
    }
    val now = nowMillis()
    val categories = core.orderedCategories()
    // In the cloud, other admins are listed too, so an admin can be made Leder/Ansatt again.
    core.users.filter { if (cloudMode) it.id != admin.id else it.role != Role.ADMIN }
        .sortedWith { a, b -> compareNorwegian(a.name, b.name) }.forEach { employee ->
        val open = employee.active && (if (cloudMode) HelperAccess.isOpen(employee.accessUntil, now) else employee.role == Role.LEADER || (employee.accessUntil ?: 0L) > now)
        if (employee.role == Role.ADMIN) {
            root.div("card user-card") {
                div("row") {
                    div("grow") {
                        div("card-title", employee.name)
                        div("muted small", "@${CompanyLogin.displayLogin(employee.username)} · Admin")
                    }
                    statusBadge(if (employee.active) t("AKTIV", "ACTIVE") else t("DEAKTIVERT", "DEACTIVATED"), if (employee.active) Tone.GREEN else Tone.GREY)
                }
                roleChips(this, employee, admin)
                div("muted small", t("Full tilgang: alle kategorier, produkter, brukere og måneder. Gjør om til Leder eller Ansatt for å begrense eller deaktivere.", "Full access: all categories, products, users and months. Change to Leader or Employee to limit or deactivate."))
            }
            return@forEach
        }
        root.div("card user-card") {
            div("row") {
                div("grow") {
                    div("card-title", employee.name)
                    div("muted small", "@${CompanyLogin.displayLogin(employee.username)} · ${roleLabel(employee.role)}")
                }
                statusBadge(if (!employee.active) t("DEAKTIVERT", "DEACTIVATED") else if (open) t("AKTIV", "ACTIVE") else t("STENGT", "CLOSED"), if (open) Tone.GREEN else Tone.GREY)
            }
            roleChips(this, employee, admin)
            if (employee.role == Role.EMPLOYEE && cloudMode) {
                helperStatusText(employee.accessUntil, now)?.let { div("muted small", it) }
                button(if (employee.accessUntil != null) t("Tilgang og QR-kode", "Access and QR code") else t("Gjør tidsbegrenset (hjelper)", "Make time-limited (helper)"),
                    "btn text", enabled = employee.active) { showHelperAccessDialog(employee.id, admin) }
            }
            if (employee.role == Role.EMPLOYEE && !cloudMode) {
                div("muted small", when {
                    !employee.active -> t("Brukeren er deaktivert.", "The user is deactivated.")
                    open -> t("Tilgang til ${formatTime(employee.accessUntil!!)}", "Access until ${formatTime(employee.accessUntil!!)}")
                    else -> t("Kan ikke logge inn for telling.", "Cannot log in to count.")
                })
                div("button-row") {
                    button(t("Åpne ${adminState.duration} min", "Open ${adminState.duration} min"), "btn primary", enabled = employee.active) {
                        core.updateUser(employee.copy(accessUntil = nowMillis() + adminState.duration * 60_000L), admin)
                    }
                    button(t("Steng", "Close"), "btn outline", enabled = employee.active) { core.updateUser(employee.copy(accessUntil = null), admin) }
                }
            } else if (!cloudMode) {
                div("muted small", if (employee.active) t("Leder har kontinuerlig innlogging; funksjoner styres av rettighetene under.", "A leader is always signed in; features are controlled by the permissions below.") else t("Lederkontoen er deaktivert.", "The leader account is deactivated."))
            }
            div("small", t("Kategorier", "Categories") + ": " + employee.allowedCategories.joinToString().ifBlank { t("ingen", "none") })
            val expanded = employee.id in adminState.expanded
            div("button-row") {
                button(if (expanded) t("Skjul rettigheter", "Hide permissions") else t("Rettigheter", "Permissions"), "btn text") {
                    if (expanded) adminState.expanded.remove(employee.id) else adminState.expanded.add(employee.id)
                    requestRender()
                }
                if (employee.active) button(t("Deaktiver", "Deactivate"), "btn text danger-text") { core.deactivateUser(employee.id, admin) }
                else button(t("Aktiver", "Activate"), "btn text") { core.updateUser(employee.copy(active = true), admin) }
            }
            if (expanded) {
                div("rights") {
                    div("rights-col") {
                        div("strong small", t("Kategorier", "Categories"))
                        categories.forEach { category ->
                            checkbox(category, category in employee.allowedCategories, "cat-${employee.id}-$category") { checked ->
                                val updated = employee.allowedCategories.toMutableSet().apply { if (checked) add(category) else remove(category) }
                                core.updateUser(employee.copy(allowedCategories = updated), admin)
                            }
                        }
                    }
                    div("rights-col") {
                        div("strong small", t("Funksjons- og feltrettigheter", "Feature and field permissions"))
                        Permission.entries.forEach { permission ->
                            checkbox(permission.label, permission in employee.permissions, "perm-${employee.id}-${permission.name}") { checked ->
                                val updated = employee.permissions.toMutableSet().apply { if (checked) add(permission) else remove(permission) }
                                core.updateUser(employee.copy(permissions = updated), admin)
                            }
                        }
                    }
                }
            }
        }
    }
}

private fun WebApp.createUserDialog(admin: UserAccount) {
    val state = object {
        var name = ""; var username = ""; var password = ""; var role = Role.EMPLOYEE
        val categories = mutableSetOf<String>(); var error: String? = null
    }
    openDialog(t("Ny bruker", "New user")) { close ->
        field(t("Navn", "Name"), state.name, "nu-name") { state.name = it }
        field(t("Brukernavn", "Username"), state.username, "nu-user", autocomplete = "off") { state.username = it }
        field(t("Passord", "Password"), state.password, "nu-pass", type = "password", autocomplete = "new-password") { state.password = it }
        div("chip-row") {
            chip(t("Ansatt", "Employee"), state.role == Role.EMPLOYEE) { state.role = Role.EMPLOYEE; requestRender() }
            chip(t("Leder", "Leader"), state.role == Role.LEADER) { state.role = Role.LEADER; requestRender() }
        }
        div("strong small", t("Tillatte kategorier", "Allowed categories"))
        div("check-grid") {
            core.orderedCategories().forEach { category ->
                checkbox(category, category in state.categories, "nu-cat-$category") { checked ->
                    if (checked) state.categories.add(category) else state.categories.remove(category)
                }
            }
        }
        errorText(state.error)
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text") { close() }
            button(t("Opprett", "Create"), "btn primary") {
                val username = state.username.trim()
                state.error = when {
                    state.name.isBlank() || username.isBlank() || state.password.isBlank() -> t("Navn, brukernavn og passord må fylles ut.", "Name, username and password are required.")
                    core.users.any { it.username.equals(username, ignoreCase = true) } -> t("Brukernavnet er allerede i bruk.", "The username is already in use.")
                    else -> null
                }
                if (state.error != null) { requestRender(); return@button }
                core.addUser(
                    UserAccount(
                        name = state.name.trim(), username = username, password = state.password, role = state.role,
                        allowedCategories = state.categories.toSet(), permissions = defaultPermissions(state.role)
                    ),
                    admin
                )
                close()
                toast(t("${state.name.trim()} er opprettet.", "${state.name.trim()} has been created."))
            }
        }
    }
}

private fun WebApp.renderAudit(root: El) {
    root.button(t("‹ Mer", "‹ More"), "btn text back") { moreView = "main"; requestRender() }
    root.pageHeader(t("Auditlogg", "Audit log"), t("Alle endringer med før- og etterverdi", "All changes with before and after values"))
    root.div("search") {
        input(auditSearch, "audit-search", "search-input", t("Bruker, produkt, felt eller verdi", "User, product, field or value"), "search") {
            auditSearch = it
            requestRender()
        }.attr("aria-label", t("Søk i auditloggen", "Search the audit log"))
    }
    val query = auditSearch.trim().lowercase()
    val productNames = core.products.associate { it.id to it.name }
    val events = core.auditEvents.filter { e ->
        query.isEmpty() || listOf(e.actorName, e.action, e.entityType, e.field, e.beforeValue, e.afterValue, e.category.orEmpty(),
            e.productId?.let { productNames[it] }.orEmpty()).any { it.lowercase().contains(query) }
    }.take(300)
    if (events.isEmpty()) {
        root.emptyCard(t("Ingen hendelser.", "No events."))
        return
    }
    root.div("list") {
        events.forEach { event ->
            div("card audit-row") {
                div("row") {
                    div("grow strong small", event.actorName)
                    span("muted tiny", formatDateTime(event.timestamp))
                }
                div("accent small strong", "${event.action} · ${event.entityType}${if (event.field.isNotBlank()) " · ${event.field}" else ""}")
                event.productId?.let { productNames[it] }?.let { div("small", it) }
                if (event.beforeValue.isNotBlank() || event.afterValue.isNotBlank()) {
                    div("small", "${event.beforeValue.ifBlank { "∅" }}  →  ${event.afterValue.ifBlank { "∅" }}")
                }
                div("muted tiny", "${event.source} · app ${event.appVersion}")
            }
        }
    }
}

/** Ansatt / Leder (/ Admin in the cloud). Making someone admin asks first. */
private fun WebApp.roleChips(parent: El, person: UserAccount, admin: UserAccount) {
    val app = this
    parent.div("chip-row") {
        chip(t("Ansatt", "Employee"), person.role == Role.EMPLOYEE) {
            app.core.updateUser(person.copy(role = Role.EMPLOYEE, permissions = defaultPermissions(Role.EMPLOYEE)), admin)
        }
        chip(t("Leder", "Leader"), person.role == Role.LEADER) {
            app.core.updateUser(person.copy(role = Role.LEADER, permissions = defaultPermissions(Role.LEADER), accessUntil = null), admin)
        }
        if (app.cloudMode && admin.role == Role.ADMIN) chip("Admin", person.role == Role.ADMIN) {
            if (person.role != Role.ADMIN) app.confirmMakeAdmin(person, admin)
        }
    }
}

private fun WebApp.confirmMakeAdmin(person: UserAccount, admin: UserAccount) {
    openDialog(t("Gjør ${person.name} til admin?", "Make ${person.name} admin?")) { close ->
        val companyName = cloud.membership?.companyName ?: t("bedriften", "the company")
        p(t("Er du sikker? ${person.name} får full tilgang i $companyName: alle kategorier og priser, produkter, brukere (også å lage nye admin), og å opprette og avslutte måneder.",
            "Are you sure? ${person.name} gets full access in $companyName: all categories and prices, products, users (including making new admins), and creating and closing months."))
        p(t("Du kan gjøre det om senere her under Brukere og tilgang.", "You can change it back later here under Users and access."), "muted small")
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text") { close() }
            button(t("Ja, gjør til admin", "Yes, make admin"), "btn primary") {
                close()
                core.updateUser(person.copy(role = Role.ADMIN, permissions = defaultPermissions(Role.ADMIN), accessUntil = null, active = true), admin)
                toast(t("${person.name} er nå admin.", "${person.name} is now admin."))
            }
        }
    }
}

/** Mer → Brukere og tilgang → + Ny person (Firebase): no open month needed. */
private fun WebApp.showCloudPersonDialog(admin: UserAccount) {
    var name = ""; var email = ""; var username = ""; var withoutEmail = false
    var helper = false; var helperHours = HelperAccess.DEFAULT_HOURS; var helperLogin: HelperLogin? = null
    var role = Role.EMPLOYEE; var error: String? = null; var busy = false
    var done: Pair<String, String>? = null
    openDialog(t("Ny person", "New person")) { close ->
        done?.let { (title, text) ->
            div("strong", title)
            text.split("\n").forEach { line -> p(line, "small") }
            helperLogin?.let { helperLoginCard(this, it) }
            dialogActions { button(t("Ferdig", "Done"), "btn primary") { close() } }
            return@openDialog
        }
        field(t("Navn", "Name"), name, "cp-name") { name = it }
        div("chip-row wrap") {
            chip(t("Med e-post", "With e-mail"), !withoutEmail && !helper) { withoutEmail = false; helper = false; error = null; requestRender() }
            chip(t("Uten e-post", "Without e-mail"), withoutEmail && !helper) { withoutEmail = true; helper = false; error = null; requestRender() }
            chip(t("Hjelper (tidsbegrenset)", "Helper (time-limited)"), helper) { helper = true; withoutEmail = true; role = Role.EMPLOYEE; error = null; requestRender() }
        }
        if (withoutEmail) {
            val normalized = CompanyLogin.normalizeUsername(username)
            field(t("Brukernavn", "Username"), username, "cp-username", autocomplete = "off",
                hint = if (normalized.isNotBlank() && normalized != username.trim()) t("Blir: $normalized", "Becomes: $normalized") else t("Til innlogging med Firmakode", "For login with company code")) { username = it }
        } else {
            field(t("E-post", "E-mail"), email, "cp-email", type = "email", autocomplete = "off",
                hint = t("Personen får en invitasjon hit og velger passordet selv (eller bruker Google).", "The person gets an invitation here and chooses the password (or uses Google).")) { email = it }
        }
        if (helper) {
            div("strong small", t("Tilgang i", "Access for"))
            helperDurationChips(helperHours) { helperHours = it; requestRender() }
            p(t("Hjelperen logger inn med QR-kode eller lenke og en startkode. Etter tiden stenges tilgangen av seg selv.",
                "The helper logs in with a QR code or link and a start code. When the time is up, access closes by itself."), "muted tiny")
        } else {
            div("strong small", t("Rolle", "Role"))
            div("chip-row") {
                chip(t("Ansatt", "Employee"), role == Role.EMPLOYEE) { role = Role.EMPLOYEE; requestRender() }
                chip(t("Leder", "Leader"), role == Role.LEADER) { role = Role.LEADER; requestRender() }
                if (admin.role == Role.ADMIN) chip("Admin", role == Role.ADMIN) { role = Role.ADMIN; requestRender() }
            }
        }
        if (role == Role.ADMIN) p(t("Admin får full tilgang: alle kategorier og priser, produkter, brukere og måneder.", "Admin gets full access: all categories and prices, products, users and months."), "muted tiny")
        errorText(error)
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text", enabled = !busy) { close() }
            button(if (busy) t("Lagrer …", "Saving …") else t("Legg til", "Add"), "btn primary", enabled = !busy) {
                error = null
                createCloudPerson(name.trim(), email.trim(), username, if (helper) Role.EMPLOYEE else role, withoutEmail,
                    onError = { error = it; requestRender() },
                    onBusy = { busy = it; requestRender() },
                    accessUntil = if (helper) HelperAccess.until(nowMillis(), helperHours) else null
                ) { _, title, text, login ->
                    helperLogin = login
                    done = title to text + if (role == Role.ADMIN) t("\n${name.trim()} er admin og ser alle kategorier.", "\n${name.trim()} is admin and sees all categories.") else t("\nVelg hovedvarer under Oversikt → Fordel arbeid.", "\nChoose main categories under Overview → Assign work.")
                    requestRender()
                }
            }
        }
    }
}

/** Mer → Språk: Norsk / English. Saved on the account in Firebase mode, else in this browser. */
fun WebApp.showLanguagePicker() {
    openDialog(t("Språk", "Language")) { close ->
        div("list") {
            AppLanguage.entries.forEach { language ->
                el("button", "card nav-card" + if (language == I18n.language) " selected" else "") {
                    attr("type", "button")
                    attr("lang", language.htmlLang)
                    div("grow card-title", language.nativeName)
                    if (language == I18n.language) span("accent strong", "✓").attr("aria-hidden", "true")
                    onClick {
                        close()
                        if (language != I18n.language) {
                            setLanguage(language)
                            toast(t("Språket er endret til norsk.", "The language is now English."))
                        }
                    }
                }
            }
        }
        p(if (cloudMode) t("Valget lagres på kontoen din og følger deg på alle enheter.", "Your choice is saved on your account and follows you on all devices.")
          else t("Valget lagres i denne nettleseren.", "Your choice is saved in this browser."), "muted small")
        dialogActions { button(t("Lukk", "Close"), "btn text") { close() } }
    }
}

/** Mer → Bedrift → Bedriftsprofil (admin): name, short name, logo, and whether the name shows next to the logo. */
fun WebApp.showCompanyProfileDialog() {
    val m = cloud.membership ?: return
    val profile = cloud.company
    var name = profile?.name ?: m.companyName
    var shortName = profile?.shortName ?: m.companyShortName
    var display = profile?.headerDisplay ?: HeaderDisplay.LOGO
    var newLogo: CompanyLogo? = null
    var removeLogo = false
    var busy = false
    var error: String? = null
    openDialog(t("Bedriftsprofil", "Company profile")) { close ->
        field(t("Navn", "Name"), name, "cp-company-name") { name = it }
        field(t("Kortnavn (valgfritt)", "Short name (optional)"), shortName, "cp-company-short",
            hint = t("Vises i toppen når det er satt.", "Shown in the header when set.")) { shortName = it }
        div("strong small", t("Logo", "Logo"))
        val shown = if (removeLogo) null else newLogo ?: cloud.logo
        if (shown != null) {
            div("logo-preview" + if (shown.darkBackground) " dark" else "") { el("img") { attr("src", "data:image/png;base64," + Crypto.base64Encode(shown.png)); attr("alt", "Logo") } }
            div("logo-bg-row") {
                span("small", t("Bakgrunn bak logoen", "Background behind the logo"))
                div("chip-row") {
                    listOf(false to t("Lys", "Light"), true to t("Mørk", "Dark")).forEach { (dark, label) ->
                        chip(label, shown.darkBackground == dark) {
                            if (shown.darkBackground != dark && !busy) {
                                busy = true
                                requestRender()
                                logoWithBackground(shown, dark) { result ->
                                    busy = false
                                    result.onSuccess { newLogo = it; removeLogo = false }.onFailure { error = it.message }
                                    requestRender()
                                }
                            }
                        }
                    }
                }
            }
        } else p(t("Ingen logo. Toppen viser bedriftsnavnet.", "No logo. The header shows the company name."), "muted small")
        div("button-row") {
            el("label", "btn outline file-button") {
                span("", if (shown == null) t("Velg logo …", "Choose logo …") else t("Bytt logo …", "Change logo …"))
                el("input") {
                    attr("type", "file")
                    attr("accept", "image/png,image/jpeg,image/webp,image/svg+xml")
                    on("change") {
                        val files = node.files
                        val file: dynamic = if (files != null && (files.length as Int) > 0) files[0] else null
                        if (file != null) {
                            busy = true
                            requestRender()
                            readLogoFile(file) { result ->
                                busy = false
                                result.onSuccess { logo ->
                                    error = CompanyLogo.problem(logo)
                                    if (error == null) { newLogo = logo; removeLogo = false }
                                }.onFailure { error = it.message }
                                requestRender()
                            }
                        }
                    }
                }
            }
            if (shown != null) button(t("Fjern logo", "Remove logo"), "btn text danger-text") { newLogo = null; removeLogo = true; requestRender() }
        }
        p(t("PNG med gjennomsiktig bakgrunn blir finest. Tomme kanter fjernes automatisk. Er logoen lys (f.eks. hvit tekst), velges mørk bakgrunn av seg selv.", "PNG with a transparent background looks best. Empty margins are removed automatically. A light logo (e.g. white text) gets a dark background by itself."), "muted tiny")
        if (shown != null) {
            div("strong small", t("Vis i toppen", "Show in the header"))
            div("chip-row wrap") {
                HeaderDisplay.entries.forEach { option ->
                    chip(option.label, display == option) { display = option; requestRender() }.attr("data-key", "cp-header-" + option.name.lowercase())
                }
            }
            p(t("De fleste logoer har navnet i seg – da holder «Bare logo».", "Most logos contain the name – then «Logo only» is enough."), "muted tiny")
        }
        errorText(error)
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text", enabled = !busy) { close() }
            button(if (busy) t("Lagrer …", "Saving …") else t("Lagre", "Save"), "btn primary", enabled = !busy) {
                if (name.isBlank()) { error = t("Skriv inn bedriftsnavnet.", "Enter the company name."); requestRender(); return@button }
                busy = true
                error = null
                requestRender()
                cloud.updateCompanyProfile(name, shortName, display) { result ->
                    result.onFailure { busy = false; error = it.message; requestRender(); return@updateCompanyProfile }
                    val logoStep: ((Result<Unit>) -> Unit) -> Unit = when {
                        newLogo != null -> { done -> cloud.saveLogo(newLogo!!, done) }
                        removeLogo -> { done -> cloud.removeLogo(done) }
                        else -> { done -> done(Result.success(Unit)) }
                    }
                    logoStep { logoResult ->
                        busy = false
                        logoResult.onSuccess { close(); toast(t("Bedriftsprofilen er lagret.", "The company profile is saved.")) }
                            .onFailure { error = it.message; requestRender() }
                    }
                }
            }
        }
    }
}
